import { FC, useMemo } from 'react';
import type { DashboardPreset, IndicatorSelectHandler } from '@/entities/dashboards';
import type { QualityZoneRanges } from '@/shared/lib';
import { useAppliedChartTableViews } from '../../model/useAppliedChartTableViews';
import { useCustomChartBindings } from '../../model/useCustomChartBindings';
import { ChartTableViewProvider } from './ChartTableViewContext';
import {
  CHART_CARD_CLASS,
  CHART_CELL_CLASS,
  CHART_GRID_CLASS,
  CHART_PLACE_CLASS,
  chartCellStyle,
  chartGridAreaStyle,
  chartGridRows,
  createChartGridTracks,
} from './chartLayout';
import { CustomChartCard, type CustomChartPanelFilters } from './CustomChartCard';

type Props = {
  preset: DashboardPreset;
  /** Фильтры панели страницы — общие для всех диаграмм пресета */
  filters: CustomChartPanelFilters;
  /** Пороги зон качества пользователя: по ним окрашиваются элементы со своим потолком */
  zoneRanges: QualityZoneRanges;
  onSelect: IndicatorSelectHandler;
  onOpenConversation: (conversationId: number, openInNewTab: boolean) => void;
};

/**
 * Сетка диаграмм применённого дашборда-пресета: порядок задан составом пресета,
 * ширина и высота каждой диаграммы — её размером, а начало строки — её признаком.
 *
 * Строка складывается из колонок: диаграммы колонки стоят одна под другой общей шириной, а нижние
 * края диаграмм строки совпадают — в том числе у диаграмм с заданной высотой: она для них наименьшая. Одиночная диаграмма — колонка из одной. Диаграммы колонок строки,
 * стоящие на одном месте сверху, начинаются на одной высоте ({@link createChartGridTracks}).
 *
 * Места диаграмм — ячейки одной сетки, а не колонки-обёртки: только так места соседних колонок
 * встают на общие дорожки.
 *
 * Разрезы и разбор выбранного считаются по всему составу сразу: разрез общий для диаграмм одного
 * имени, а связанная диаграмма ждёт выбора в своей ведущей.
 *
 * Вид колонок таблиц пишется по правилам фильтров дашборда ({@link useAppliedChartTableViews}).
 */
export const DashboardPresetGrid: FC<Props> = ({ preset, filters, zoneRanges, onSelect, onOpenConversation }) => {
  const bindings = useCustomChartBindings(preset.charts);
  const tableViews = useAppliedChartTableViews(preset.id);
  const layout = useMemo(() => {
    const tracks = createChartGridTracks();
    const places = chartGridRows(preset.charts).flatMap((row) => tracks.addRow(row).places);
    return { places, gridStyle: tracks.gridStyle() };
  }, [preset.charts]);

  return (
    <ChartTableViewProvider store={tableViews}>
      <div className={CHART_GRID_CLASS} style={layout.gridStyle}>
        {layout.places.map(({ chartIndex, area }) => {
          const chart = preset.charts[chartIndex];
          return (
            <div key={chart.id} className={CHART_PLACE_CLASS} style={chartGridAreaStyle(area)}>
              <div className={CHART_CELL_CLASS} style={chartCellStyle(chart.height)}>
                <div className={CHART_CARD_CLASS}>
                  <CustomChartCard
                    chart={chart}
                    filters={filters}
                    zoneRanges={zoneRanges}
                    bindings={bindings.get(chart.id)}
                    onSelect={onSelect}
                    onOpenConversation={onOpenConversation}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ChartTableViewProvider>
  );
};
